/**
 * AngularJS Integration Reference Component
 * 
 * Issue #69 - Simplified Legacy Integration Approach
 * 
 * Reference component showing React/AngularJS integration patterns.
 * This demonstrates what's possible for complex integration scenarios,
 * but TECDocChart uses simple page-to-page navigation instead.
 * 
 * Available at /integration-test route for documentation purposes.
 */

import React, { useState } from 'react';

const AngularIntegrationTest: React.FC = () => {
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    timestamp: number;
  }>>([]);

  const addNotification = (notification: Omit<typeof notifications[0], 'id' | 'timestamp'>) => {
    const newNotification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };
    setNotifications(prev => [...prev, newNotification]);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>React/AngularJS Integration Reference</h2>
      
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7', 
        borderRadius: '4px',
        marginBottom: '20px'
      }}>
        <h4>📋 Architecture Decision: Simple Navigation</h4>
        <p>
          TECDocChart uses <strong>simple page-to-page navigation</strong> between React and AngularJS modules 
          rather than complex embedding frameworks. This approach is:
        </p>
        <ul>
          <li>✅ Simpler to maintain and debug</li>
          <li>✅ Lower risk for healthcare workflows</li>
          <li>✅ More appropriate for independent medical modules</li>
          <li>✅ Standard web development patterns</li>
        </ul>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Current Navigation Pattern</h3>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #dee2e6',
          borderRadius: '4px' 
        }}>
          <p><strong>React Dashboard</strong> → Standard Link → <strong>AngularJS Module</strong></p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <a 
              href="/dashboard" 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#007bff', 
                color: 'white', 
                textDecoration: 'none',
                borderRadius: '4px'
              }}
            >
              Dashboard (React)
            </a>
            <a 
              href="/patients/" 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                textDecoration: 'none',
                borderRadius: '4px'
              }}
            >
              Patients (AngularJS)
            </a>
            <a 
              href="/encounters/" 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                textDecoration: 'none',
                borderRadius: '4px'
              }}
            >
              Encounters (AngularJS)
            </a>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Future Migration Pattern</h3>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#e8f5e8', 
          border: '1px solid #c3e6c3',
          borderRadius: '4px' 
        }}>
          <p><strong>React Dashboard</strong> → Standard Link → <strong>React Module</strong></p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#007bff', 
                color: 'white', 
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Dashboard (React) ✅
            </button>
            <button 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Patients (React) 🔄 Future
            </button>
            <button 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Encounters (React) 🔄 Future
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>React Component Demo</h3>
        <p>Simple React functionality for testing:</p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <button 
            onClick={() => addNotification({ type: 'success', message: 'React component working!' })}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add Success Message
          </button>
          <button 
            onClick={() => addNotification({ type: 'info', message: `Current time: ${new Date().toLocaleTimeString()}` })}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#17a2b8', 
              color: 'white', 
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add Time Message
          </button>
          <button 
            onClick={clearNotifications}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#6c757d', 
              color: 'white', 
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Messages
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Messages</h3>
        {notifications.length === 0 ? (
          <p style={{ color: '#6c757d' }}>No messages</p>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div 
                key={notification.id}
                style={{ 
                  padding: '8px 12px',
                  marginBottom: '4px',
                  backgroundColor: 
                    notification.type === 'error' ? '#f8d7da' :
                    notification.type === 'warning' ? '#fff3cd' :
                    notification.type === 'success' ? '#d4edda' : '#d1ecf1',
                  border: `1px solid ${
                    notification.type === 'error' ? '#f5c6cb' :
                    notification.type === 'warning' ? '#ffeaa7' :
                    notification.type === 'success' ? '#c3e6cb' : '#bee5eb'
                  }`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <strong>{notification.type.toUpperCase()}:</strong> {notification.message}
                <br />
                <small style={{ color: '#6c757d' }}>
                  {new Date(notification.timestamp).toLocaleTimeString()}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ fontSize: '12px', color: '#6c757d', borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
        <h4>Available Reference Documentation</h4>
        <ul>
          <li>📄 <code>SIMPLE_MIGRATION_STRATEGY.md</code> - Chosen approach documentation</li>
          <li>📄 <code>ANGULAR_INTEGRATION.md</code> - Advanced framework reference (if needed)</li>
          <li>🔧 <code>AngularApiHelpers.ts</code> - API utilities for existing AngularJS modules</li>
          <li>📊 <code>CLAUDE.md</code> - Updated architecture documentation</li>
        </ul>
        
        <p><strong>Next Steps:</strong></p>
        <ol>
          <li>Build React versions of medical modules (Patients, Encounters, Reports)</li>
          <li>Use simple navigation links between React and AngularJS pages</li>
          <li>Migrate modules one at a time based on business priority</li>
        </ol>
      </div>
    </div>
  );
};

export default AngularIntegrationTest;